import React, {
	useCallback,
	useRef,
} from 'react';

type EditableElement = HTMLInputElement | HTMLTextAreaElement;

export const FOCUSABLE_SELECTOR = [
	'input:not([disabled]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"])',
	'textarea:not([disabled])',
].join(', ');

const isEditableElement = (element: EventTarget): element is EditableElement => (
	element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
);

const getCellInput = (cell: Element | undefined) => (
	cell?.querySelector<EditableElement>(FOCUSABLE_SELECTOR) ?? null
);

export const findVerticalTarget = (cell: HTMLTableCellElement, direction: 1 | -1) => {
	const getSibling = (row: Element | null) => (
		direction === 1 ? row?.nextElementSibling : row?.previousElementSibling
	) ?? null;

	let row = getSibling(cell.parentElement);

	while (row != null) {
		const input = row instanceof HTMLTableRowElement ? getCellInput(row.cells[cell.cellIndex]) : null;

		if (input != null) {
			return input;
		}

		row = getSibling(row);
	}

	return null;
};

const setNativeValue = (element: EditableElement, value: string) => {
	const prototype = element instanceof HTMLTextAreaElement ?
		HTMLTextAreaElement.prototype :
		HTMLInputElement.prototype;

	Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(element, value);
	element.dispatchEvent(new Event('input', { bubbles: true }));
};

export const useSpreadsheetNavigation = () => {
	const initialValues = useRef(new WeakMap<EditableElement, string>());

	const onFocusCapture = useCallback((e: React.FocusEvent<HTMLElement>) => {
		if (isEditableElement(e.target)) {
			initialValues.current.set(e.target, e.target.value);
		}
	}, []);

	const onKeyDownCapture = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
		const { target } = e;

		if (!isEditableElement(target) || target.closest('.ant-picker') != null) {
			return;
		}

		const isCombobox = target.getAttribute('role') === 'combobox';

		if (isCombobox && target.getAttribute('aria-expanded') === 'true') {
			return;
		}

		if (e.key === 'Escape' && !isCombobox) {
			const initialValue = initialValues.current.get(target);

			if (initialValue != null && initialValue !== target.value) {
				e.stopPropagation();
				setNativeValue(target, initialValue);
			}

			return;
		}

		const cell = target.closest('td');

		if (e.key !== 'Enter' || cell == null) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		const nextInput = findVerticalTarget(cell, e.shiftKey ? -1 : 1);

		if (nextInput == null) {
			target.blur();

			return;
		}

		nextInput.focus();
		nextInput.select();
	}, []);

	return {
		onFocusCapture,
		onKeyDownCapture,
	};
};
